import { useState, useEffect, useContext } from "react"
import { AuthContext } from "../context/AuthContext"
import axios from "axios"
import { format } from "date-fns"
import { Eye, Search, Trash2, Clock, CheckCircle, Pencil } from "lucide-react"
import OrderPendingModal from "../components/OrderPendingModal"
import EditOrderContentModal from "../components/EditOrderContentModal"
import DeleteOrderModal from "../components/DeleteOrderModal"
import { showToast } from "../utils/toastUtils"


const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const WebPendingConfirm = () => {
  const { token } = useContext(AuthContext)
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedOrderId, setSelectedOrderId] = useState(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Delete State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [orderToDelete, setOrderToDelete] = useState(null)

  // Reusable Input Style (matches CreateOrder)
  const inputStyle = "w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 text-gray-700 placeholder-gray-400 shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none hover:border-blue-300"

  if (token) axios.defaults.headers.common["x-auth-token"] = token

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true)
        const res = await axios.get(`${BASE_URL}/api/orders`)
        const filtered = res.data.filter(order => order.status === "Pending Confirm")
        setOrders(filtered)
      } catch (err) {
        // setError(err.response?.data?.message || "Error fetching orders")
        showToast("Error", err.response?.data?.message || "Error fetching orders", "error")
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [])


  const handleDeleteClick = (order) => {
    setOrderToDelete(order)
    setIsDeleteModalOpen(true)
  }

  const confirmDeleteOrder = async (orderId, reason) => {
    try {
      await axios.delete(`${BASE_URL}/api/orders/${orderId}`, {
        data: { reason } // Pass reason if backend supports it, though usually standard delete doesn't need body unless specified
      })
      showToast("Success", "Order deleted successfully", "success")
      setOrders(prev => prev.filter(order => order._id !== orderId))
      setIsDeleteModalOpen(false)
    } catch (err) {
      console.error("Delete error:", err)
      showToast("Error", err.response?.data?.message || "Failed to delete order", "error")
    }
  }


  const filteredOrders = orders.filter(order => {
    const lower = searchTerm.toLowerCase()
    return (
      order.customerName.toLowerCase().includes(lower) ||
      order.phone.includes(searchTerm) ||
      (order.orderId && order.orderId.toLowerCase().includes(lower))
    )
  })

  const handleViewOrderDetails = (orderId) => {
    setSelectedOrderId(orderId)
    setIsDetailsModalOpen(true)
  }

  const handleOrderUpdated = (orderId) => {
    setOrders(prev => prev.filter(order => order._id !== orderId))
  }

  const formatAddress = (order) => {
    return [order.addressLine1, order.addressLine2, order.city].filter(Boolean).join(", ")
  }

  // Edit State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [orderToEdit, setOrderToEdit] = useState(null)

  const handleEditOrder = (orderId) => {
    setOrderToEdit(orderId)
    setIsEditModalOpen(true)
  }

  const handleEditSuccess = (orderId) => {
    showToast("Success", "Order updated successfully", "success")
    // Refresh orders to show new totals
    setOrders(prev => prev.map(o => {
      if (o._id === orderId) {
        // We need to fetch the updated order, but for now we will just trigger a reload or rely on the modal closing.
        // Actually, the easiest way is to re-fetch or just let the user see it on refresh.
        // Better: trigger a re-fetch of all orders or just that one.
        return o
      }
      return o
    }))
    // Trigger re-fetch for simplicity to get exact calculations from backend
    // Or we can just let `fetchOrders` run again if we had it exposed.
    window.location.reload(); // Simple reload for now to ensure data consistency
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-500" /> Pending Confirmation
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Manage orders waiting for confirmation. Verify details and process them.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1 rounded-full">
              {filteredOrders.length} Pending
            </span>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6 max-w-md">
          <div className="relative group">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              className={`${inputStyle} pl-10`}
              placeholder="Search by name, phone, or Order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-500">Loading pending orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <div className="bg-gray-100 rounded-full p-4 mb-4">
                <CheckCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">No Pending Orders</h3>
              <p className="text-gray-500 mt-1 max-w-sm">
                Great job! There are no orders waiting for confirmation right now.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order._id} className="hover:bg-blue-50/30 transition-colors duration-150 group">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">#{order.orderId || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">{order.customerName}</span>
                          <span className="text-xs text-gray-500 truncate max-w-[150px]" title={formatAddress(order)}>
                            {formatAddress(order)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(order.createdAt), "MMM d, yyyy")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-900">
                          Rs. {order.total.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                   ${(order.paymentMethod === 'cod' || !order.paymentMethod)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {(order.paymentMethod === 'cod' || !order.paymentMethod) ? 'COD' : order.paymentMethod}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewOrderDetails(order._id)}
                            className="p-2 bg-white border border-gray-200 rounded-lg text-blue-600 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-all shadow-sm group-hover:shadow-md"
                            title="Review Order"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditOrder(order._id)}
                            className="p-2 bg-white border border-gray-200 rounded-lg text-amber-600 hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700 transition-all shadow-sm group-hover:shadow-md"
                            title="Edit Items"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(order)}
                            className="p-2 bg-white border border-gray-200 rounded-lg text-red-500 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-all shadow-sm group-hover:shadow-md"
                            title="Delete Order"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      <OrderPendingModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        orderId={selectedOrderId}
        token={token}
        onOrderUpdated={handleOrderUpdated}
      />

      <EditOrderContentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        orderId={orderToEdit}
        token={token}
        onUpdateSuccess={handleEditSuccess}
      />

      <DeleteOrderModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        order={orderToDelete}
        onDelete={confirmDeleteOrder}
      />

    </div>
  )
}

export default WebPendingConfirm